import { LitElement, html, css, svg, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import "./log-view.js";
import {
  parseBngraph,
  closure,
  layout,
  NODE_W,
  NODE_H,
  type Graph,
  type GraphNode,
  type Layout,
} from "./bngraph.js";

// The longest label a node fits at the diagram's font size.
const LABEL_MAX = 24;

/** middle cuts a long name in its middle: names often differ only at the end. */
function middle(name: string): string {
  if (name.length <= LABEL_MAX) return name;
  const head = Math.ceil((LABEL_MAX - 1) / 2);
  return name.slice(0, head) + "…" + name.slice(name.length - (LABEL_MAX - 1 - head));
}

/**
 * graph-view draws the dependency graph of a .bngraph file: one column per
 * kind, the artefact on the left, and a line from each node to what it depends
 * on. Selecting a node keeps what it uses and what uses it at full strength
 * and dims the rest; "Only related" hides the rest. The lines that leave the
 * artefact are drawn only for a selection, as they would cover the diagram.
 * A node table gives the same data as text. Text that is not such a file is
 * shown as it is.
 *
 *   <graph-view .text=${text}></graph-view>
 */
@customElement("graph-view")
export class GraphView extends LitElement {
  @property() text = "";

  @state() private selected = "";
  @state() private onlyRelated = false;

  private graph: Graph | null = null;
  private related: Set<string> | null = null;
  private layout?: Layout;

  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 0;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .bar {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      padding-bottom: var(--bino-space-sm);
      flex-shrink: 0;
    }
    .hint {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .spacer {
      flex: 1;
    }
    log-view {
      flex: 1;
      margin-top: var(--bino-space-sm);
    }
    .body {
      flex: 1;
      min-height: 0;
      display: flex;
      gap: var(--bino-space-md);
    }
    .canvas {
      flex: 1;
      min-width: 0;
      overflow: auto;
      background: var(--bino-surface-subtle);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
    }
    svg {
      display: block;
    }
    .col,
    .divider {
      font-size: 11px;
      font-weight: 600;
      fill: var(--bino-text-secondary);
    }
    .col {
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .edge {
      fill: none;
      stroke: var(--bino-gray-400);
      stroke-width: 1;
      opacity: 0.45;
    }
    .edge.dim {
      opacity: 0.12;
    }
    .edge.hot {
      stroke: var(--bino-primary);
      stroke-width: 2;
      opacity: 1;
    }
    .node {
      cursor: pointer;
      outline: none;
    }
    .node rect {
      fill: var(--bino-surface);
      stroke: var(--bino-border-light);
      stroke-width: 1;
    }
    .node text {
      font-family: var(--bino-font-mono);
      font-size: 11px;
      fill: var(--bino-text);
    }
    .node:hover rect {
      stroke: var(--bino-border-hover);
    }
    .node:focus-visible rect {
      stroke: var(--bino-primary);
      stroke-width: 2;
    }
    .node.dim {
      opacity: 0.3;
    }
    .node.selected rect {
      fill: var(--bino-surface-active);
      stroke: var(--bino-primary);
      stroke-width: 2;
    }
    .node.selected text {
      font-weight: 600;
    }
    .panel {
      flex: 0 0 20rem;
      overflow: auto;
      font-size: var(--bino-font-size-base);
    }
    .k {
      margin-top: var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-text-secondary);
    }
    .k:first-child {
      margin-top: 0;
    }
    .v {
      font-family: var(--bino-font-mono);
      overflow-wrap: anywhere;
    }
    ul {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    li {
      font-family: var(--bino-font-mono);
      overflow-wrap: anywhere;
    }
    .link {
      background: none;
      border: none;
      padding: 0;
      font: inherit;
      text-align: left;
      color: var(--bino-primary);
      text-decoration: underline;
      cursor: pointer;
      overflow-wrap: anywhere;
    }
    .link:hover {
      color: var(--bino-primary-hover);
    }
    details {
      flex-shrink: 0;
      max-height: 40%;
      overflow: auto;
      margin-top: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
    }
    summary {
      cursor: pointer;
      color: var(--bino-text-secondary);
    }
    table {
      border-collapse: collapse;
      margin-top: var(--bino-space-sm);
      font-family: var(--bino-font-mono);
    }
    th,
    td {
      padding: 0.125rem var(--bino-space-md) 0.125rem 0;
      text-align: left;
      vertical-align: top;
    }
    th {
      font-family: var(--bino-font-sans);
      color: var(--bino-text-secondary);
    }
  `;

  override willUpdate(changed: PropertyValues) {
    if (changed.has("text")) {
      this.graph = parseBngraph(this.text);
      this.selected = "";
      this.onlyRelated = false;
    }
    const g = this.graph;
    if (!g) return;
    this.related = this.selected ? closure(g, this.selected) : null;
    this.layout = layout(g, this.onlyRelated && this.related ? this.related : undefined);
  }

  private toggle(id: string) {
    this.selected = this.selected === id ? "" : id;
    if (!this.selected) this.onlyRelated = false;
  }

  /** jump selects a node from the side panel and brings it into view. */
  private async jump(id: string) {
    this.selected = id;
    await this.updateComplete;
    for (const el of this.renderRoot.querySelectorAll<SVGGElement>(".node")) {
      if (el.dataset.id === id) el.scrollIntoView({ block: "center", inline: "center" });
    }
  }

  private onKey(e: KeyboardEvent, id: string) {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    this.toggle(id);
  }

  private renderSvg(l: Layout) {
    const rel = this.related;
    const hot = (from: string, to: string) => rel !== null && rel.has(from) && rel.has(to);
    const rest = l.edges.filter((e) => !hot(e.from, e.to) && !e.fromRoot);
    const lit = l.edges.filter((e) => hot(e.from, e.to));
    return html`<svg
      width=${l.width}
      height=${l.height}
      viewBox="0 0 ${l.width} ${l.height}"
      role="group"
      aria-label="Dependency graph"
    >
      ${l.columns.map(
        (c) => svg`<text class="col" x=${c.x} y=${c.y + 16}>${c.kind} (${c.count})</text>`,
      )}
      ${l.dividers.map(
        (d) =>
          svg`<text class="divider" x=${d.x} y=${d.y + 16}>Used by the report only (${d.count})</text>`,
      )}
      ${rest.map((e) => svg`<path class="edge ${rel ? "dim" : ""}" d=${e.d}></path>`)}
      ${l.nodes.map((p) => {
        const id = p.node.id;
        const cls = id === this.selected ? "selected" : rel && !rel.has(id) ? "dim" : "";
        return svg`<g
          class="node ${cls}"
          data-id=${id}
          transform="translate(${p.x},${p.y})"
          tabindex="0"
          role="button"
          aria-pressed=${id === this.selected}
          aria-label="${p.node.kind} ${p.node.name}"
          @click=${() => this.toggle(id)}
          @keydown=${(e: KeyboardEvent) => this.onKey(e, id)}
        >
          <title>${p.node.name}</title>
          <rect width=${NODE_W} height=${NODE_H} rx="6"></rect>
          <text x="8" y="16">${middle(p.node.name)}</text>
        </g>`;
      })}
      ${lit.map((e) => svg`<path class="edge hot" d=${e.d}></path>`)}
    </svg>`;
  }

  private renderRefs(g: Graph, ids: string[]) {
    if (ids.length === 0) return html`<div class="v">—</div>`;
    return html`<ul>
      ${ids.map(
        (id) =>
          html`<li>
            ${g.byId.has(id)
              ? html`<button class="link" @click=${() => this.jump(id)}>${id}</button>`
              : id}
          </li>`,
      )}
    </ul>`;
  }

  private renderPanel(g: Graph, n: GraphNode) {
    return html`<aside class="panel" aria-label="Selected node">
      <div class="k">Kind</div>
      <div class="v">${n.kind}</div>
      <div class="k">Name</div>
      <div class="v">${n.name}</div>
      <div class="k">Hash</div>
      <div class="v">${n.hash || "—"}</div>
      <div class="k">Details</div>
      <div class="v">${n.details || "—"}</div>
      <div class="k">Depends on (${n.deps.length})</div>
      ${this.renderRefs(g, n.deps)}
      <div class="k">Used by (${g.usedBy.get(n.id)?.length ?? 0})</div>
      ${this.renderRefs(g, g.usedBy.get(n.id) ?? [])}
    </aside>`;
  }

  override render() {
    const g = this.graph;
    const l = this.layout;
    if (!g || !l) {
      return html`<p class="hint">This file is not a graph table. Showing it as text.</p>
        <log-view nowrap .text=${this.text}></log-view>`;
    }
    const node = g.byId.get(this.selected);
    return html`
      <div class="bar">
        <p class="hint">
          ${g.nodes.length} nodes.
          ${node ? "Select the node again to clear." : "Select a node to see what it is linked to."}
        </p>
        <span class="spacer"></span>
        <bn-checkbox
          label="Only related"
          .checked=${this.onlyRelated}
          ?disabled=${!node}
          @change=${(e: CustomEvent) => (this.onlyRelated = e.detail.checked)}
        ></bn-checkbox>
      </div>
      <div class="body">
        <div class="canvas">${this.renderSvg(l)}</div>
        ${node ? this.renderPanel(g, node) : nothing}
      </div>
      <details>
        <summary>Node table</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Kind</th>
              <th scope="col">Name</th>
              <th scope="col">Hash</th>
              <th scope="col">Depends on</th>
            </tr>
          </thead>
          <tbody>
            ${g.nodes.map(
              (n) =>
                html`<tr>
                  <td>${n.kind}</td>
                  <td>${n.name}</td>
                  <td>${n.hash}</td>
                  <td>${n.deps.join(", ")}</td>
                </tr>`,
            )}
          </tbody>
        </table>
      </details>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "graph-view": GraphView;
  }
}
